import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type { ILoqCredentialsDto, ILoqOptionDto, ILoqProjectConfigurationDto } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Input } from "@/design-system/Input";
import { Notice } from "@/design-system/Notice";
import { SectionCard } from "@/design-system/SectionCard";
import { Select } from "@/design-system/Select";
import { Tiles } from "@/design-system/Tiles";
import { QUERY_KEYS } from "@/query-keys";
import type { TranslationKey } from "@/translations";
import { useTranslation } from "@/translations";

import { BulkOperationResults, BulkOperationReview, type BulkOperationReviewField } from "./BulkOperationReview";
import type { CredentialMode, ILoqBulkEditableValues, ILoqOperation, PreparedBulkProject } from "./helpers";
import {
  commonValue,
  CONFIGURATION_REQUEST_CONCURRENCY,
  getErrorMessage,
  mapWithConcurrency,
  prepareILoqProject,
} from "./helpers";
import type { BulkOperationResult, PortfolioIntegrationProject } from "./types";

const CREDENTIAL_MODES = ["keep", "missing", "replace"] as const;
const ILOQ_OPERATIONS = ["configure", "enable", "disable"] as const;

const CREDENTIAL_MODE_LABELS = {
  keep: "page.portfolio-integrations.credentials.keep",
  missing: "page.portfolio-integrations.credentials.missing",
  replace: "page.portfolio-integrations.credentials.replace",
} as const satisfies Record<CredentialMode, TranslationKey>;

const ILOQ_OPERATION_LABELS = {
  configure: "page.portfolio-integrations.operation.configuration",
  enable: "page.portfolio-integrations.iloq.operation.enable",
  disable: "page.portfolio-integrations.iloq.operation.disable",
} as const satisfies Record<ILoqOperation, TranslationKey>;

type BulkILoqEditorProps = {
  rows: PortfolioIntegrationProject<ILoqProjectConfigurationDto>[];
  selectedProjectIds: Set<string>;
  onContinueToProjectSetup: () => void;
};

export function BulkILoqEditor({
  rows,
  selectedProjectIds,
  onContinueToProjectSetup,
}: BulkILoqEditorProps): React.ReactNode {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const selectedRows = useMemo(
    () => rows.filter((row) => row.canManage && selectedProjectIds.has(row.project.id)),
    [rows, selectedProjectIds],
  );
  const rowsRef = useRef(rows);
  const selectionKey = JSON.stringify([...selectedProjectIds].sort());

  const [credentialMode, setCredentialMode] = useState<CredentialMode>("keep");
  const [operation, setOperation] = useState<ILoqOperation>("configure");
  const [stage, setStage] = useState<"edit" | "review" | "results">("edit");
  const [values, setValues] = useState<ILoqBulkEditableValues>({
    serviceAddress: "",
    customerCode: "",
    username: "",
    password: "",
  });
  const [lockGroups, setLockGroups] = useState<ILoqOptionDto[]>([]);
  const [realEstates, setRealEstates] = useState<ILoqOptionDto[]>([]);
  const [zones, setZones] = useState<ILoqOptionDto[]>([]);
  const [lockGroupId, setLockGroupId] = useState("");
  const [realEstateId, setRealEstateId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [initialOptionIds, setInitialOptionIds] = useState({ lockGroupId: "", realEstateId: "", zoneId: "" });
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [results, setResults] = useState<BulkOperationResult[]>([]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    const currentProjectIds = new Set<string>(JSON.parse(selectionKey));
    const currentRows = rowsRef.current.filter((row) => currentProjectIds.has(row.project.id));
    setValues({
      serviceAddress: commonValue(currentRows.map((row) => row.configuration.serviceAddress)),
      customerCode: commonValue(currentRows.map((row) => row.configuration.customerCode)),
      username: commonValue(currentRows.map((row) => row.configuration.username)),
      password: "",
    });
    const nextOptionIds = {
      lockGroupId: commonValue(currentRows.map((row) => row.configuration.lockGroupId)),
      realEstateId: commonValue(currentRows.map((row) => row.configuration.realEstateId)),
      zoneId: commonValue(currentRows.map((row) => row.configuration.zoneId)),
    };
    setLockGroupId(nextOptionIds.lockGroupId);
    setRealEstateId(nextOptionIds.realEstateId);
    setZoneId(nextOptionIds.zoneId);
    setInitialOptionIds(nextOptionIds);
    setCredentialMode("keep");
    setLockGroups([]);
    setRealEstates([]);
    setZones([]);
    setConnectionError(null);
    setStage("edit");
  }, [selectionKey]);

  const connectionSource = selectedRows[0];
  const credentialsForConnection = (): ILoqCredentialsDto => {
    if (credentialMode === "keep" && connectionSource?.configured) {
      return {
        serviceAddress: connectionSource.configuration.serviceAddress ?? "",
        customerCode: connectionSource.configuration.customerCode ?? "",
        username: connectionSource.configuration.username ?? "",
      };
    }

    return {
      serviceAddress: values.serviceAddress,
      customerCode: values.customerCode,
      username: values.username,
      password: values.password || undefined,
    };
  };

  const connect = async () => {
    if (!connectionSource) {
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);
    try {
      const response = await api.postIntegrationsIloqLockGroupsV1(credentialsForConnection(), {
        headers: { [ProjectHeaderName]: connectionSource.project.id },
      });
      setLockGroups(response.data);
      setLockGroupId((current) => current || response.data[0]?.id || "");
      setRealEstates([]);
      setZones([]);
    } catch (error) {
      setConnectionError(getErrorMessage(error, t("page.integration.iloq.status.connection-failed")));
    } finally {
      setIsConnecting(false);
    }
  };

  const loadOptions = async () => {
    if (!connectionSource || !lockGroupId) {
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);
    try {
      const response = await api.postIntegrationsIloqOptionsV1(lockGroupId, credentialsForConnection(), {
        headers: { [ProjectHeaderName]: connectionSource.project.id },
      });
      setRealEstates(response.data.realEstates);
      setZones(response.data.zones);
      setRealEstateId((current) => current || response.data.realEstates[0]?.id || "");
      setZoneId((current) => current || response.data.zones[0]?.id || "");
    } catch (error) {
      setConnectionError(getErrorMessage(error, t("page.integration.iloq.status.connection-failed")));
    } finally {
      setIsConnecting(false);
    }
  };

  const connectionCredentials = credentialsForConnection();
  const preparedProjects = selectedRows.map((row) =>
    prepareILoqProject({
      row,
      operation,
      credentialMode,
      values,
      connectionCredentials,
      lockGroupId,
      realEstateId,
      zoneId,
      messages: {
        notConfigured: t("page.portfolio-integrations.preflight.iloq-not-configured"),
        incomplete: t("page.portfolio-integrations.preflight.iloq-incomplete"),
        password: t("page.portfolio-integrations.preflight.iloq-password"),
        mappings: t("page.portfolio-integrations.preflight.iloq-mappings"),
      },
    }),
  );
  const validProjects = preparedProjects.filter((project) => !project.reason);
  const skippedProjects = preparedProjects.filter((project) => project.reason) as Array<
    PreparedBulkProject<ILoqProjectConfigurationDto> & { reason: string }
  >;

  const fields = useMemo(() => {
    const result: BulkOperationReviewField[] = [];
    if (credentialMode === "missing") {
      result.push({
        label: t("page.portfolio-integrations.credentials.title"),
        value: t("page.portfolio-integrations.credentials.missing"),
      });
    } else if (credentialMode === "replace") {
      result.push(
        {
          label: t("page.portfolio-integrations.credentials.title"),
          value: t("page.portfolio-integrations.credentials.replace"),
        },
        { label: t("page.integration.iloq.form.service-address"), value: values.serviceAddress.trim() },
        { label: t("page.integration.iloq.form.customer-code"), value: values.customerCode.trim() },
        { label: t("page.integration.iloq.form.username"), value: values.username.trim() },
        { label: t("page.integration.iloq.form.password"), value: "••••••••" },
      );
    }
    if (lockGroupId !== initialOptionIds.lockGroupId) {
      result.push({ label: t("page.integration.iloq.form.lock-group"), value: lockGroupId });
    }
    if (realEstateId !== initialOptionIds.realEstateId) {
      result.push({ label: t("page.integration.iloq.form.real-estate"), value: realEstateId });
    }
    if (zoneId !== initialOptionIds.zoneId) {
      result.push({ label: t("page.integration.iloq.form.zone"), value: zoneId });
    }
    if (operation === "enable") {
      result.push({
        label: t("page.portfolio-integrations.activation.title"),
        value: t("page.portfolio-integrations.iloq.operation.enable"),
      });
    } else if (operation === "disable") {
      result.push({
        label: t("page.portfolio-integrations.activation.title"),
        value: t("page.portfolio-integrations.iloq.operation.disable"),
      });
    }

    return result;
  }, [credentialMode, initialOptionIds, lockGroupId, operation, realEstateId, t, values, zoneId]);

  const apply = async (projectIds?: string[]) => {
    if (validProjects.length === 0) {
      return;
    }

    setIsApplying(true);
    try {
      const targets = validProjects.filter((project) => !projectIds || projectIds.includes(project.id));
      const settled = await mapWithConcurrency(targets, CONFIGURATION_REQUEST_CONCURRENCY, async (project) => {
        try {
          await api.putIntegrationsIloqV1(project.payload, {
            headers: { [ProjectHeaderName]: project.id },
          });

          return { status: "fulfilled", value: project } as const;
        } catch (reason) {
          return { status: "rejected", reason } as const;
        }
      });

      const updatedResults = settled.map((result, index): BulkOperationResult => {
        const project = targets[index];

        return result.status === "fulfilled"
          ? { projectId: project.id, projectName: project.name, status: "updated" }
          : {
              projectId: project.id,
              projectName: project.name,
              status: "failed",
              reason: getErrorMessage(result.reason, t("page.portfolio-integrations.results.update-failed")),
            };
      });
      await Promise.all([
        ...updatedResults
          .filter((result) => result.status === "updated")
          .map((result) =>
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.INTEGRATION_ILOQ_CONFIGURATION(result.projectId) }),
          ),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_INTEGRATION_CONFIGURATIONS("iloq") }),
      ]);

      if (projectIds) {
        setResults((current) => [
          ...current.filter((result) => !projectIds.includes(result.projectId)),
          ...updatedResults,
        ]);
      } else {
        setResults([
          ...updatedResults,
          ...skippedProjects.map((project) => ({
            projectId: project.id,
            projectName: project.name,
            status: "skipped" as const,
            reason: project.reason,
          })),
        ]);
      }
      setStage("results");
    } finally {
      setIsApplying(false);
    }
  };

  if (stage === "results") {
    return (
      <BulkOperationResults
        results={results}
        isRetrying={isApplying}
        onRetry={(projectIds) => void apply(projectIds)}
        onContinue={onContinueToProjectSetup}
      />
    );
  }

  const canConnect = Boolean(
    connectionSource &&
    ((credentialMode === "keep" && connectionSource.configured) ||
      (values.serviceAddress.trim() && values.customerCode.trim() && values.username.trim() && values.password.trim())),
  );
  const lockGroupOptions = includeSelectedOption(lockGroups, lockGroupId);
  const realEstateOptions = includeSelectedOption(realEstates, realEstateId);
  const zoneOptions = includeSelectedOption(zones, zoneId);

  return (
    <>
      <SectionCard
        title={t("page.portfolio-integrations.iloq.bulk-editable-title")}
        description={t("page.portfolio-integrations.iloq.bulk-editable-description")}
      >
        <FormField label={t("page.portfolio-integrations.credentials.title")}>
          <Select
            width="wide"
            items={CREDENTIAL_MODES}
            selected={credentialMode}
            keySelector={(item) => item}
            renderOption={(item) => t(CREDENTIAL_MODE_LABELS[item])}
            onChange={setCredentialMode}
          />
        </FormField>

        {credentialMode !== "keep" && (
          <Tiles columns={{ base: 1, container: { md: 2 } }} gap="md">
            <FormField label={t("page.integration.iloq.form.service-address")}>
              <Input
                value={values.serviceAddress}
                onChange={(event) => setValues((current) => ({ ...current, serviceAddress: event.target.value }))}
              />
            </FormField>
            <FormField label={t("page.integration.iloq.form.customer-code")}>
              <Input
                value={values.customerCode}
                onChange={(event) => setValues((current) => ({ ...current, customerCode: event.target.value }))}
              />
            </FormField>
            <FormField label={t("page.integration.iloq.form.username")}>
              <Input
                value={values.username}
                onChange={(event) => setValues((current) => ({ ...current, username: event.target.value }))}
              />
            </FormField>
            <FormField
              label={t("page.integration.iloq.form.password")}
              description={t("page.portfolio-integrations.credentials.password-hidden")}
            >
              <Input
                type="password"
                value={values.password}
                onChange={(event) => setValues((current) => ({ ...current, password: event.target.value }))}
              />
            </FormField>
          </Tiles>
        )}

        <Inline gap="sm" align="center">
          <Button variant="soft" disabled={!canConnect} isLoading={isConnecting} onClick={() => void connect()}>
            {t("page.integration.iloq.action.connect")}
          </Button>
          {connectionSource && (
            <span className="text-caption text-text-secondary">
              {t("page.portfolio-integrations.iloq.connection-source", { project: connectionSource.project.name })}
            </span>
          )}
        </Inline>

        {connectionError && <Notice variant="danger" message={connectionError} />}

        {(lockGroups.length > 0 || lockGroupId) && (
          <Tiles columns={{ base: 1, container: { md: 3 } }} gap="md">
            <FormField label={t("page.integration.iloq.form.lock-group")}>
              <Select
                items={lockGroupOptions}
                selected={lockGroupOptions.find((option) => option.id === lockGroupId)}
                keySelector={(option) => option.id}
                renderOption={(option) => option.name}
                onChange={(option) => {
                  setLockGroupId(option.id);
                  setRealEstateId("");
                  setZoneId("");
                }}
              />
            </FormField>
            <Inline align="end" wrap={false}>
              <Button
                variant="soft"
                disabled={!lockGroupId}
                isLoading={isConnecting}
                onClick={() => void loadOptions()}
              >
                {t("page.integration.iloq.action.reload")}
              </Button>
            </Inline>
          </Tiles>
        )}

        {(realEstates.length > 0 || realEstateId || zones.length > 0 || zoneId) && (
          <Tiles columns={{ base: 1, container: { md: 2 } }} gap="md">
            <FormField label={t("page.integration.iloq.form.real-estate")}>
              <Select
                items={realEstateOptions}
                selected={realEstateOptions.find((option) => option.id === realEstateId)}
                keySelector={(option) => option.id}
                renderOption={(option) => option.name}
                onChange={(option) => setRealEstateId(option.id)}
              />
            </FormField>
            <FormField label={t("page.integration.iloq.form.zone")}>
              <Select
                items={zoneOptions}
                selected={zoneOptions.find((option) => option.id === zoneId)}
                keySelector={(option) => option.id}
                renderOption={(option) => option.name}
                onChange={(option) => setZoneId(option.id)}
              />
            </FormField>
          </Tiles>
        )}

        {lockGroupId && realEstateId && zoneId && (
          <Badge variant="success">{t("page.portfolio-integrations.iloq.connection-ready")}</Badge>
        )}

        <FormField
          label={t("page.portfolio-integrations.activation.title")}
          description={t("page.portfolio-integrations.activation.description")}
        >
          <Select
            width="standard"
            items={ILOQ_OPERATIONS}
            selected={operation}
            keySelector={(item) => item}
            renderOption={(item) => t(ILOQ_OPERATION_LABELS[item])}
            onChange={setOperation}
          />
        </FormField>

        <Form.Footer
          renderPrimaryAction={() => (
            <Button
              disabled={selectedRows.length === 0 || (operation === "configure" && fields.length === 0)}
              onClick={() => setStage("review")}
            >
              {t("page.portfolio-integrations.action.review")}
            </Button>
          )}
        />
      </SectionCard>
      {stage === "review" && (
        <BulkOperationReview
          title={t("page.portfolio-integrations.review.title")}
          description={
            operation === "configure"
              ? t("page.portfolio-integrations.review.activation-preserved")
              : t("page.portfolio-integrations.review.activation-explicit")
          }
          fields={fields}
          validProjects={validProjects}
          skippedProjects={skippedProjects}
          isApplying={isApplying}
          onBack={() => setStage("edit")}
          onApply={() => void apply()}
        />
      )}
    </>
  );
}

function includeSelectedOption(options: ILoqOptionDto[], selectedId: string): ILoqOptionDto[] {
  if (!selectedId || options.some((option) => option.id === selectedId)) {
    return options;
  }

  return [{ id: selectedId, name: selectedId }, ...options];
}
